/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useLayoutEffect, useRef } from 'react';

/** Matches `--duration-medium`; only used to bound the cleanup timer. */
const FALLBACK_CLEAR_MS = 400;

/**
 * Animates a block between two content-driven heights.
 *
 * CSS alone cannot do this. A transition fires on a change of *computed*
 * value, and a block sized by its content computes to `height: auto` before
 * and after — what changed is the used value. `interpolate-size` does not
 * help either: it makes `auto` interpolable against a length, not `auto`
 * against `auto`. So the height has to be pinned to a number for the
 * duration of the animation and handed back to the content afterwards.
 *
 * The sequence is the standard one: after React has committed the new DOM,
 * read the new height, write the old one back, force a reflow so the
 * browser accepts it as a starting point, then write the new one and let
 * the CSS transition run. Clearing the inline height on `transitionend`
 * returns the block to `auto`, so it keeps growing with its content
 * afterwards rather than being frozen at whatever it measured.
 *
 * @param key - Changes when the content changes; the list passes its row count.
 * @returns A ref to attach to the element whose height should animate.
 */
export function useAnimatedHeight<T extends HTMLElement>(key: unknown) {
  const ref = useRef<T>(null);
  const previousHeight = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    /*
     * Measure the content, never a height a previous run pinned.
     *
     * Without this the hook poisons itself: an interrupted animation leaves
     * an inline height behind, the next run measures *that* instead of the
     * rows, stores it as the new truth, and from then on the block is
     * frozen at a stale number. With `overflow: hidden` on it the list is
     * clipped mid-row and the scroller above has nothing left to scroll --
     * rows are loaded, on the page, and unreachable.
     */
    element.style.height = '';
    const nextHeight = element.getBoundingClientRect().height;
    const lastHeight = previousHeight.current;
    previousHeight.current = nextHeight;

    // Nothing to animate from on first paint, nor when the height held.
    if (lastHeight === null || Math.round(lastHeight) === Math.round(nextHeight)) return;
    // Someone who asked for less motion is not asking for a slower version.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    element.style.height = `${lastHeight}px`;
    void element.offsetHeight;
    element.style.height = `${nextHeight}px`;

    const release = () => {
      element.style.height = '';
      element.removeEventListener('transitionend', release);
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
    element.addEventListener('transitionend', release);
    // A transition that never starts — an interrupted one, a tab that was
    // backgrounded — would otherwise leave the block pinned to a stale
    // pixel height and unable to grow again.
    timer.current = setTimeout(release, FALLBACK_CLEAR_MS);

    /*
     * Release on unmount and on every re-run, not just drop the listeners.
     *
     * Clearing the listener and the timer without clearing the height takes
     * away both ways the block had of being handed back to its content and
     * leaves it pinned. A content change that arrives mid-transition is the
     * common case, not the exotic one -- the list re-renders once per
     * loaded batch -- so this ran on nearly every page of an infinite
     * scroll.
     */
    return () => {
      element.removeEventListener('transitionend', release);
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      element.style.height = '';
    };
  }, [key]);

  return ref;
}
